import React, { useEffect, useMemo, useState } from "react";
import { api, type EventDTO } from "../lib/api";
import { getStats } from "../lib/preferences";
import {
  downloadForOffline,
  getOfflineStatus,
  prefetchPhotosBackground,
} from "../lib/offline";
import { refreshSnapshot, purgeApiCaches } from "../lib/snapshot";
import { shareForEvent } from "../lib/share";
import { ShareButton } from "../components/ShareButton";
import { AffiliateStrip } from "../components/AffiliateStrip";
import { FestivalMap } from "../components/FestivalMap";
import { IconCloudCheck, IconCloudDownload, IconRefresh } from "../components/icons";

type ViewMode = "list" | "map";

/**
 * Region filter — "All" plus any region that actually has events in the
 * catalogue, sorted by event count desc so the biggest region comes first.
 */
const ALL_REGIONS = "all" as const;
type RegionFilter = typeof ALL_REGIONS | string;

export function Events({
  onPick,
}: {
  onPick: (event: EventDTO) => void;
}) {
  const [events, setEvents] = useState<EventDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [region, setRegion] = useState<RegionFilter>(ALL_REGIONS);
  const [mode, setMode] = useState<ViewMode>("list");
  const [, forceRerender] = useState(0);

  useEffect(() => {
    api.events().then(setEvents).catch(e => setError(String(e)));
    const onOffline = () => forceRerender(n => n + 1);
    window.addEventListener("offline:changed", onOffline);
    return () => window.removeEventListener("offline:changed", onOffline);
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await purgeApiCaches();
      const s = await refreshSnapshot();
      const fresh = s?.events ?? (await api.events());
      setEvents(fresh);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div data-testid="view-events">
      <div className="section-head">
        <div>
          <span className="super">CHECK THE LINE UP</span>
          <h2>Festivals</h2>
        </div>
        <button
          className="chip"
          onClick={onRefresh}
          disabled={refreshing}
          aria-label="Refresh data"
          data-testid="refresh-data"
          title="Re-fetch the catalogue (useful when set times drop)"
        >
          <IconRefresh /> {refreshing ? "refreshing…" : "refresh"}
        </button>
      </div>
      <p
        style={{
          padding: "0 18px 6px",
          color: "var(--ink-dim)",
          fontSize: 14,
          lineHeight: 1.45,
        }}
      >
        Swipe. Schedule. Survive the festival. Pick an event to get started.
        Tap the cloud icon to pre-download an event for full offline use.
      </p>

      {error && <div className="empty">Could not load events: {error}</div>}
      {!events && !error && <div className="empty">Loading events…</div>}

      {events && events.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "0 14px 6px",
            }}
          >
            <div className="seg" role="tablist" aria-label="View mode" data-testid="view-mode">
              <button
                role="tab"
                aria-pressed={mode === "list"}
                onClick={() => setMode("list")}
                data-testid="mode-list"
              >
                List
              </button>
              <button
                role="tab"
                aria-pressed={mode === "map"}
                onClick={() => setMode("map")}
                data-testid="mode-map"
              >
                Map
              </button>
            </div>
          </div>
          <RegionChips events={events} region={region} onChange={setRegion} />
        </>
      )}

      {events && mode === "map" && (
        <div style={{ padding: "0 14px 20px" }}>
          <FestivalMap
            events={events.filter(ev => region === ALL_REGIONS || ev.region === region)}
            onPick={onPick}
          />
        </div>
      )}

      <div
        style={{
          padding: "4px 14px 20px",
          display: mode === "list" ? "grid" : "none",
          gap: 12,
        }}
      >
        {events
          ?.filter(ev => region === ALL_REGIONS || ev.region === region)
          .map(ev => (
            <EventCard key={ev.slug} event={ev} onPick={onPick} />
          ))}

        {events && region !== ALL_REGIONS &&
          events.filter(e => e.region === region).length === 0 && (
            <div
              className="empty"
              data-testid="region-empty"
              style={{ textAlign: "center" }}
            >
              No festivals in {region} yet.
            </div>
          )}

        <div
          style={{
            marginTop: 10,
            padding: 16,
            borderRadius: 14,
            border: "1px dashed var(--panel-edge)",
            color: "var(--ink-faint)",
            fontSize: 13,
            textAlign: "center",
            lineHeight: 1.5,
          }}
        >
          More festivals coming soon.<br />
          <span style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            // powered by thelineup engine
          </span>
        </div>
      </div>
    </div>
  );
}

function RegionChips({
  events,
  region,
  onChange,
}: {
  events: EventDTO[];
  region: RegionFilter;
  onChange: (r: RegionFilter) => void;
}) {
  // Count events per region. "All" is always first; other regions sort by
  // count desc so the biggest cohort is prominent.
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of events) {
      if (!e.region) continue;
      m.set(e.region, (m.get(e.region) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [events]);

  return (
    <div
      role="tablist"
      aria-label="Filter by region"
      data-testid="region-filter"
      style={{
        display: "flex",
        gap: 6,
        padding: "0 14px 12px",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
      }}
    >
      <button
        role="tab"
        className="chip"
        aria-pressed={region === ALL_REGIONS}
        onClick={() => onChange(ALL_REGIONS)}
        data-testid="region-all"
        style={{ whiteSpace: "nowrap" }}
      >
        All · {events.length}
      </button>
      {counts.map(([r, n]) => (
        <button
          key={r}
          role="tab"
          className="chip"
          aria-pressed={region === r}
          onClick={() => onChange(r)}
          data-testid={`region-${r.toLowerCase().replace(/\s+/g, "-")}`}
          style={{ whiteSpace: "nowrap" }}
        >
          {r} · {n}
        </button>
      ))}
    </div>
  );
}

function EventCard({ event, onPick }: { event: EventDTO; onPick: (e: EventDTO) => void }) {
  const stats = getStats(event.slug, event.artistCount);
  const offline = getOfflineStatus(event.slug);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const onClick = () => {
    // Fire-and-forget prefetch of event photos *before* navigating so
    // there's a head start on the cache warming for this event.
    void (async () => {
      const artists = await api.artists(event.slug);
      const urls = artists.map(a => a.photoUrl).filter((u): u is string => !!u);
      prefetchPhotosBackground(urls);
    })();
    onPick(event);
  };

  const onDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      const artists = await api.artists(event.slug);
      await downloadForOffline(event, artists, (done, total) => {
        setProgress({ done, total });
      });
    } finally {
      setDownloading(false);
      setProgress(null);
    }
  };

  const ready = offline?.status === "ready";
  const partial = offline?.status === "partial";

  return (
    <button
      onClick={onClick}
      data-testid="event-card"
      data-slug={event.slug}
      className="deck"
      style={{
        position: "relative",
        padding: 16,
        borderRadius: 16,
        textAlign: "left",
        overflow: "hidden",
        ["--glow" as never]: event.color ?? "#ff6a13",
        background:
          "linear-gradient(155deg, color-mix(in oklab, var(--glow) 28%, transparent) 0%, #0f1114 70%)," +
          "linear-gradient(180deg, #161a1f, #0a0b0e)",
        color: "var(--ink)",
        minHeight: 140,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: "-30% -10% auto auto",
          width: "60%",
          aspectRatio: "1 / 1",
          background:
            "radial-gradient(60% 60% at 50% 50%, var(--glow), transparent 70%)",
          filter: "blur(32px)",
          opacity: 0.55,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily: "var(--fs-mono)",
              fontSize: 10,
              letterSpacing: "0.22em",
              color: "var(--ink-faint)",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            {fmtDateRange(event.startDate, event.endDate)} · {event.year}
          </div>
          <h3 style={{ margin: 0, fontSize: 24, letterSpacing: "-0.01em", color: event.color ?? "var(--amber-hot)" }}>
            {event.name}
          </h3>
          {event.subtitle && (
            <div style={{ marginTop: 2, fontSize: 13, color: "var(--ink-dim)" }}>{event.subtitle}</div>
          )}
          {event.location && (
            <div
              style={{
                marginTop: 8,
                fontFamily: "var(--fs-mono)",
                fontSize: 10,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--ink-faint)",
              }}
            >
              ◷ {event.location}
            </div>
          )}
          {event.region && (
            <div
              style={{
                marginTop: 4,
                fontFamily: "var(--fs-mono)",
                fontSize: 9,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--ink-faint)",
                opacity: 0.75,
              }}
            >
              {event.countryName ?? event.country ?? ""}{" "}
              {event.countryName ? "·" : ""} {event.region}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {event.artistCount === 0 ? (
            <div
              className="lcd"
              data-testid="event-lineup-tba"
              style={{
                fontFamily: "var(--fs-mono)",
                fontSize: 10,
                letterSpacing: "0.2em",
                padding: "4px 8px",
                color: event.color ?? "var(--amber)",
                border: `1px solid ${event.color ?? "var(--amber)"}`,
              }}
            >
              LINEUP TBA
            </div>
          ) : (
            <>
              <div
                style={{
                  fontFamily: "var(--fs-mono)",
                  fontSize: 28,
                  lineHeight: 1,
                  color: event.color ?? "var(--amber)",
                  textShadow: `0 0 10px ${event.color ?? "#ff6a13"}`,
                }}
              >
                {event.artistCount}
              </div>
              <div
                style={{
                  fontFamily: "var(--fs-mono)",
                  fontSize: 9,
                  letterSpacing: "0.2em",
                  color: "var(--ink-faint)",
                  textTransform: "uppercase",
                  marginTop: 4,
                }}
              >
                artists
              </div>
              <div
                className="lcd"
                style={{ marginTop: 8, fontSize: 10, padding: "3px 6px" }}
              >
                {stats.like + stats.superlike} liked
              </div>
            </>
          )}
        </div>
      </div>

      {/* Affiliate strip — Tickets / Stay / Map */}
      <AffiliateStrip event={event} />

      {/* Offline-readiness button */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 6,
          marginTop: 10,
          borderTop: "1px solid rgba(255,255,255,0.06)",
          paddingTop: 8,
        }}
      >
        {progress && (
          <span
            style={{
              fontFamily: "var(--fs-mono)",
              fontSize: 10,
              letterSpacing: "0.1em",
              color: "var(--ink-faint)",
              marginRight: "auto",
            }}
          >
            downloading… {progress.done}/{progress.total}
          </span>
        )}
        {ready && !progress && (
          <span
            style={{
              fontFamily: "var(--fs-mono)",
              fontSize: 10,
              letterSpacing: "0.12em",
              color: "var(--green)",
              marginRight: "auto",
            }}
          >
            offline-ready · {offline!.ok}/{offline!.total}
          </span>
        )}
        {partial && !progress && (
          <span
            style={{
              fontFamily: "var(--fs-mono)",
              fontSize: 10,
              letterSpacing: "0.12em",
              color: "var(--yellow)",
              marginRight: "auto",
            }}
          >
            partial · {offline!.ok}/{offline!.total}
          </span>
        )}
        {!progress && !ready && !partial && <span style={{ marginRight: "auto" }} />}
        <ShareButton
          variant="icon"
          label={`Share ${event.name}`}
          data-testid="share-event"
          content={() => shareForEvent(event)}
        />
        <button
          className="chip"
          onClick={onDownload}
          disabled={downloading}
          data-testid="download-offline"
          title={
            ready
              ? "Already downloaded — tap to re-cache"
              : "Download photos + data for offline use at the venue"
          }
          style={{
            color: ready ? "var(--green)" : "var(--ink-dim)",
            borderColor: ready ? "rgba(81,224,120,0.5)" : undefined,
          }}
        >
          {ready ? <IconCloudCheck /> : <IconCloudDownload />}
          {downloading ? "…" : ready ? "saved" : "offline"}
        </button>
      </div>
    </button>
  );
}

function fmtDateRange(start: string, end: string): string {
  const [_y1, m1, d1] = start.split("-");
  const [_y2, m2, d2] = end.split("-");
  const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const mo1 = MONTHS[parseInt(m1!, 10) - 1] ?? m1;
  const mo2 = MONTHS[parseInt(m2!, 10) - 1] ?? m2;
  if (mo1 === mo2) return `${mo1} ${parseInt(d1!, 10)} — ${parseInt(d2!, 10)}`;
  return `${mo1} ${parseInt(d1!, 10)} — ${mo2} ${parseInt(d2!, 10)}`;
}
